import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AuthLayout } from '@/components/AuthLayout'
import { useAuth } from '@/lib/AuthContext'
import { RECOVERY_HASH_TOKENS, supabase } from '@/lib/supabaseClient'
import { Telemetry } from '@/lib/telemetry'

/**
 * Landing page for the Supabase password-reset link.
 *
 * Supabase redirects the user here with recovery tokens in the URL hash
 * fragment. The SDK's auto-detect (`detectSessionInUrl: true`) _tries_ to
 * parse those tokens and establish a session on client init, but it
 * silently fails in edge cases (see RECOVERY_HASH_TOKENS in
 * supabaseClient.ts — the SDK's internal `_getUser` call before
 * `_saveSession` can drop the session). The `ensureSession` effect below
 * is the belt-and-suspenders path: snapshot-backed `setSession()` retry
 * on mount + once more at submit time, so `updateUser()` always has a
 * valid session to run against.
 *
 * A2F-633 coupling (optimization only, post-architectural-fix):
 * AuthContext's init effect and its onAuthStateChange listener still
 * short-circuit on this exact pathname. That's now purely a
 * performance / UX-cleanliness optimization — skip an otherwise
 * pointless /auth/me round-trip and avoid writing a spurious
 * "commons_access_denied" flash during the transient reset-link
 * state. It is no longer load-bearing for session preservation:
 * `denyCommonsSession` no longer calls `signOut`. If the pathname
 * literal is changed here without matching AuthContext, the worst
 * case is one extra /auth/me call + a stale flash on /auth/login
 * after navigate('/'), not a destroyed session.
 */
export function UpdatePasswordPage() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const sessionEnsuredRef = useRef(false)

  /**
   * A2F-633 (second attempt): ensure the recovery session is actually in
   * place before the user submits. The SDK's auto-detect on mount
   * _should_ handle this, but it silently fails in edge cases (see the
   * note on RECOVERY_HASH_TOKENS in supabaseClient.ts). Run once on
   * mount: if getSession returns null, fall back to the hash-token
   * snapshot and explicit setSession.
   */
  useEffect(() => {
    let cancelled = false

    async function ensureSession() {
      if (sessionEnsuredRef.current) return
      sessionEnsuredRef.current = true

      const { data } = await supabase.auth.getSession()
      if (cancelled) return
      if (data.session) {
        // SDK's auto-detect worked — nothing to do.
        return
      }

      // No session in storage. If the page was loaded from a recovery
      // link, RECOVERY_HASH_TOKENS captured the tokens synchronously at
      // module init (before the SDK could consume the hash). Retry
      // explicitly.
      if (!RECOVERY_HASH_TOKENS) {
        // Either the user arrived without a recovery link, or the hash
        // was already clean when the module loaded (e.g., a bookmarked
        // /auth/new-password visit). updateUser will fail with a clear
        // error — nothing we can do frontend-side.
        Telemetry.errorSwallowed({
          error_type: 'password_reset_no_session',
          operation: 'password_reset_ensure_session',
          screen_name: '/auth/new-password',
          error_message: 'no session and no recovery hash tokens captured',
        })
        return
      }

      const { error: setErr } = await supabase.auth.setSession(
        RECOVERY_HASH_TOKENS,
      )
      if (cancelled) return
      if (setErr) {
        // Supabase rejected the tokens. Most likely: link already used,
        // expired, or the recovery token hasn't been issued yet (race).
        // The user will see the submit-time error; surface the root
        // cause to telemetry for debugging.
        Telemetry.errorSwallowed({
          error_type: 'password_reset_set_session_failed',
          operation: 'password_reset_ensure_session',
          screen_name: '/auth/new-password',
          error_message: setErr.message ?? 'unknown',
        })
      }
    }

    ensureSession()
    return () => {
      cancelled = true
    }
  }, [])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)

    // Defense-in-depth: if by submit time there's still no session (the
    // mount effect's setSession failed, or it hasn't completed yet),
    // give setSession one last try here before updateUser. Idempotent
    // if the session is already in place.
    const { data: preSubmit } = await supabase.auth.getSession()
    if (!preSubmit.session && RECOVERY_HASH_TOKENS) {
      await supabase.auth.setSession(RECOVERY_HASH_TOKENS)
    }

    const { error: err } = await supabase.auth.updateUser({ password })
    setLoading(false)

    if (err) {
      // Most likely: reset link was expired or already used.
      setError(
        'Could not update password. The reset link may have expired — please request a new one.',
      )
      Telemetry.errorSwallowed({
        error_type: 'password_reset_failure',
        operation: 'password_reset_complete',
        screen_name: '/auth/new-password',
        error_message: err.message ?? 'unknown',
      })
      return
    }
    Telemetry.passwordResetCompleted()
    // A2F-633 follow-up: AuthContext's init effect and onAuthStateChange
    // handler both short-circuit on this route, so `user` is still null
    // right now. updateUser() fires USER_UPDATED, not SIGNED_IN, so the
    // listener wouldn't hydrate it anyway. Call refresh() explicitly to
    // populate user state before navigating — otherwise ProtectedRoute
    // at `/` bounces straight to /auth/login.
    //
    // If refresh's /auth/me 403s with commons_access_denied (the
    // underlying A2F-634 data-state issue), denyCommonsSession writes
    // the flash, refresh itself calls setUser(null), and ProtectedRoute
    // will redirect to /auth/login with the flash — acceptable fallback:
    // the password change did succeed, but the user can't access
    // commons anyway. Supabase session is preserved (A2F-633
    // architectural fix) so once A2F-634 lands, a refresh is enough to
    // recover without re-requesting a reset link.
    await refresh()
    navigate('/', { replace: true })
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Choose a new password for your CTDCommons account."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="password"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            New password
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>

        <div>
          <label
            htmlFor="confirm"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Confirm password
          </label>
          <input
            id="confirm"
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>

        {error && (
          <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-2 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors disabled:opacity-50"
        >
          {loading ? 'Updating...' : 'Update password'}
        </button>
      </form>

      <div className="mt-6 text-center text-xs text-slate-500">
        <Link to="/" className="text-brand-orange hover:underline">
          &larr; Back to sign in
        </Link>
      </div>
    </AuthLayout>
  )
}
